import { useEffect, useMemo, useRef, useState } from "react"
import { Link } from "react-router"
import { AnimatePresence, motion, useMotionValue, useSpring, useTransform } from "motion/react"
import {
  ArrowRight,
  ArrowUpRight,
  Award04,
  CalendarCheck01,
  CheckCircle,
  ChevronRight,
  Coins03,
  LineChartUp01,
  Rocket02,
  Stars02,
  Target04,
  Trophy01,
  Wallet01,
  X,
  ZapFast,
  FileCheck02,
} from "@untitledui/icons"

import { useAuth } from "@/lib/auth/auth-context"
import { useOrganization } from "@/lib/organization/organization-context"
import { useTeam, useTeamMembers } from "@/lib/api/use-teams"
import { NotificationsBell } from "./notifications-bell"
import type {
  ApiEstimate,
  CompetitionModuleKey,
  DealershipRewardTrigger,
  TeamSettingsMetadata,
} from "@/lib/api/types"
import type { CompetitionLiveState } from "@/lib/dashboard/competition-state"
import type { CompetitionCadence } from "@/lib/dashboard/defaults"
import { Roles } from "@/lib/team/roles"
import {
  computeAdvisorStats,
  type AdvisorStats,
  type LeaderboardSnapshot,
  type RecentUnlock,
} from "@/lib/dashboard/advisor-stats"
import { cx } from "@/lib/utils/cx"
import { SATour } from "./sa-tour"

// Service Advisor adoption dashboard.
//
// Render order: TopStrip (greeting + earned/streak pills + notifications
// bell) → WalletGlance → EarningsCard (Today/Week/Month switcher) →
// StreakCard → WeeklyLeaderboardCard → StartEstimateHero →
// RecentEstimatesList → celebration/status banner slot →
// ActiveCompetitionsCard → FooterLinks.
//
// Competitions are auto-active: every module the manager enables renders
// as a live row — no join/leave. Streak + leaderboard cards only appear
// when a config that pays for them is on (no nudging advisors to chase
// numbers the dealership doesn't reward).

interface SADashboardViewProps {
  user: { id: string; name: string; role: string; email: string } | null
  estimates: ApiEstimate[]
  firstName: string
  greet: string
  today: string
  canQuick: boolean
}

const STAGGER = { staggerChildren: 0.05, delayChildren: 0.05 }
const RISE = {
  hidden: { opacity: 0, y: 12 },
  show: { opacity: 1, y: 0, transition: { type: "spring", stiffness: 240, damping: 26 } },
}

export function SADashboardView({ estimates, firstName, canQuick }: SADashboardViewProps) {
  const { user } = useAuth()
  const { activeTeamId } = useOrganization()
  const { data: team } = useTeam(activeTeamId)
  const { data: members } = useTeamMembers(activeTeamId ? Number(activeTeamId) : null)

  const meta = (team?.metadata ?? {}) as TeamSettingsMetadata
  const advisorCount = useMemo(
    () => (members ?? []).filter((m) => m.role === Roles.ServiceAdvisor).length,
    [members],
  )

  const stats = useMemo(
    () =>
      computeAdvisorStats({
        estimates,
        members,
        currentUserId: user?.id ? Number(user.id) : null,
        currentUserFirstName: firstName,
        goalsRewards: meta.goalsRewards,
        baseline: meta.serviceLane,
        activeAdvisorCount: advisorCount,
      }),
    [estimates, members, user?.id, firstName, meta.goalsRewards, meta.serviceLane, advisorCount],
  )

  const [tourDismissed, setTourDismissed] = useState(false)
  // Accumulate dismissed unlock keys — storing only the last one would let
  // two same-day unlocks resurrect each other (dismiss B → find() re-matches
  // A → dismiss A → re-matches B, forever).
  const [dismissedUnlocks, setDismissedUnlocks] = useState<ReadonlySet<string>>(
    () => new Set(),
  )
  const [weeklyDismissed, setWeeklyDismissed] = useState(false)
  const [monthlyDismissed, setMonthlyDismissed] = useState(false)
  const [paidDismissed, setPaidDismissed] = useState<string | null>(null)
  // The most recent per-paid wallet event in the last 24h — fires the
  // celebration banner. Body-shop completions are the highest-emotion
  // money moment, and the rarest signal, so they earn the banner slot.
  // MUST live above the early-return tour gate so the hook count stays
  // stable across renders (Rules of Hooks).
  const freshPay = useMemo(() => {
    const cutoff = Date.now() - 24 * 60 * 60 * 1000
    return (
      stats.wallet.recentEvents.find(
        (e) => e.trigger === "perEstimateCompleted" && e.occurredAt.getTime() >= cutoff,
      ) ?? null
    )
  }, [stats.wallet.recentEvents])
  // Estimates whose per-scan payout was suppressed by the 30-day same-VIN
  // dedupe — the recent list tags these "duplicate — no payout". Sourced from
  // the uncapped stats set, not the 20-event display feed, so an older
  // duplicate never loses its tag once the feed fills up.
  const duplicateVinIds = useMemo(
    () => new Set(stats.duplicateEstimateIds),
    [stats.duplicateEstimateIds],
  )
  // Identity under leaderboard names (name → role · department). Lives up
  // here with the other hooks — the tour early-return sits below.
  const memberMeta = useMemo(
    () =>
      new Map(
        (members ?? []).map((m) => [
          String(m.id),
          { role: m.role, department: m.department },
        ]),
      ),
    [members],
  )
  const quickHref = "/estimates/quick?return=/dashboard"

  // First-time tour gate.
  if (stats.stage === "not-activated" && !tourDismissed) {
    return (
      <SATour
        firstName={firstName}
        goalsRewards={meta.goalsRewards}
        baseline={meta.serviceLane}
        activeAdvisorCount={advisorCount}
        onComplete={() => setTourDismissed(true)}
        onSkip={() => setTourDismissed(true)}
      />
    )
  }

  // Banner priority. Each banner has its own dismiss flag so dismissing
  // a monthly celebration doesn't suppress an unrelated weekly one. Goal-
  // hit banners only fire when the matching bonus is actually enabled —
  // in the wallet-first model, hitting a target the manager didn't pay
  // for shouldn't fake a celebration moment.
  const monthlyGoalHit =
    stats.bonuses.monthlyHit &&
    stats.month.perAdvisorTarget > 0 &&
    stats.month.count >= stats.month.perAdvisorTarget &&
    !monthlyDismissed
  const weeklyGoalHit =
    !monthlyGoalHit &&
    stats.bonuses.weeklyHit &&
    stats.week.perAdvisorTarget > 0 &&
    stats.week.count >= stats.week.perAdvisorTarget &&
    !weeklyDismissed
  const freshUnlock = !monthlyGoalHit && !weeklyGoalHit
    ? (stats.recentUnlocks.find((u) => !dismissedUnlocks.has(unlockKey(u))) ?? null)
    : null
  const showReEngage = !monthlyGoalHit && !weeklyGoalHit && !freshUnlock && stats.modes.reEngagementNeeded
  const showComeback = !monthlyGoalHit && !weeklyGoalHit && !freshUnlock && !showReEngage && stats.modes.comeback

  const showFreshPay = freshPay && paidDismissed !== freshPay.label

  const perEstimate =
    stats.effectiveGoals.rewards?.perEstimateSubmitted?.monetary?.enabled
      ? stats.effectiveGoals.rewards.perEstimateSubmitted.monetary.amount ?? 0
      : 0
  const perPaid =
    stats.effectiveGoals.rewards?.perEstimateCompleted?.monetary?.enabled
      ? stats.effectiveGoals.rewards.perEstimateCompleted.monetary.amount ?? 0
      : 0

  // Streak/leaderboard cards only render when a config that pays for them
  // is on — same rule as the cockpit band's streak rail stat.
  const streakConfigOn =
    stats.bonuses.activationAny ||
    stats.bonuses.streak4Week ||
    stats.competitions.some((c) => c.key === "streakBonus")
  const showWeeklyBoard =
    !!stats.leaderboardWeekly &&
    (stats.bonuses.leaderboard ||
      stats.competitions.some((c) => c.key === "weeklyVolumeLeaderboard"))

  const teamIdNum = activeTeamId ? Number(activeTeamId) : null
  const storeWeeklyTarget = Number(meta.goalsRewards?.weeklyEstimateTarget ?? 0) || 0

  return (
    <div className="relative min-h-screen bg-gradient-to-b from-zinc-50 to-white pb-24">
      <div aria-hidden className="pointer-events-none fixed inset-0">
        <div className="absolute -top-32 right-[-200px] size-[680px] rounded-full bg-emerald-200/20 blur-3xl" />
        <div className="absolute top-1/2 -left-40 size-[440px] rounded-full bg-amber-100/30 blur-3xl" />
      </div>

      <motion.div
        initial="hidden"
        animate="show"
        variants={{ show: { transition: STAGGER } }}
        className="relative max-w-[560px] mx-auto px-4 md:px-6 pt-5 md:pt-8 space-y-4"
      >
        {/* THE COCKPIT — one charcoal band, the product's signature voice
            (dark band + emerald money). Today's pace, today's pay, and the
            streak/week/month rail composed in one object — no floating
            pills, no tile clutter, no slogans. */}
        <AdvisorHeroBand
          firstName={firstName}
          bell={<NotificationsBell teamId={teamIdNum} />}
          stats={stats}
          showStreak={streakConfigOn}
        />

        {/* HERO — the work the advisor came here to do, one thumb-reach
            below the cockpit. */}
        {canQuick && (
          <StartEstimateHero
            href={quickHref}
            perEstimate={perEstimate}
            perPaid={perPaid}
          />
        )}

        {/* WALLET GLANCE — small pill linking to the full /wallet screen. */}
        <WalletGlance
          available={stats.wallet.available}
          pending={stats.wallet.pending}
          nextPayoutDate={stats.wallet.nextPayoutDate}
        />

        {/* EARNINGS — Revolut-style switcher between Today / Week / Month. */}
        <EarningsCard stats={stats} />

        {/* WEEKLY LEADERBOARD — podium + Mon-start standings in scans + $. */}
        {showWeeklyBoard && stats.leaderboardWeekly && (
          <WeeklyLeaderboardCard
            board={stats.leaderboardWeekly}
            competitions={stats.competitions}
            memberMeta={memberMeta}
            storeWeeklyTarget={storeWeeklyTarget}
          />
        )}

        {/* Recent estimates — the bridge between "I did work" and "what
            each piece is worth right now." Each row shows status + earned
            + pending money. Tap drills into /estimates/:id. */}
        <RecentEstimatesList
          estimates={estimates}
          currentUserId={user?.id ? Number(user.id) : null}
          perEstimate={perEstimate}
          perPaid={perPaid}
          duplicateVinIds={duplicateVinIds}
        />

        {/* Celebration + status banners — single slot, priority chain. */}
        <AnimatePresence mode="wait">
          {showFreshPay && freshPay ? (
            <FreshPayBanner
              key={`paid-${freshPay.label}`}
              event={freshPay}
              onDismiss={() => setPaidDismissed(freshPay.label)}
            />
          ) : monthlyGoalHit ? (
            <GoalHitBanner
              key="goal-hit-month"
              kind="month"
              count={stats.month.count}
              target={stats.month.perAdvisorTarget}
              moneyEarned={
                stats.effectiveGoals.rewards?.monthlyGoalHit?.monetary?.enabled
                  ? stats.effectiveGoals.rewards.monthlyGoalHit.monetary.amount ?? 0
                  : 0
              }
              streakBonusMoney={0}
              onDismiss={() => setMonthlyDismissed(true)}
            />
          ) : weeklyGoalHit ? (
            <GoalHitBanner
              key="goal-hit-week"
              kind="week"
              count={stats.week.count}
              target={stats.week.perAdvisorTarget}
              moneyEarned={
                stats.effectiveGoals.rewards?.weeklyGoalHit?.monetary?.enabled
                  ? stats.effectiveGoals.rewards.weeklyGoalHit.monetary.amount ?? 0
                  : 0
              }
              streakBonusMoney={
                stats.effectiveGoals.rewards?.weeklyStreak4Bonus?.monetary?.enabled
                  ? stats.effectiveGoals.rewards.weeklyStreak4Bonus.monetary.amount ?? 0
                  : 0
              }
              onDismiss={() => setWeeklyDismissed(true)}
            />
          ) : freshUnlock ? (
            <UnlockBanner
              key={`unlock-${unlockKey(freshUnlock)}`}
              unlock={freshUnlock}
              onDismiss={() =>
                setDismissedUnlocks((prev) => new Set(prev).add(unlockKey(freshUnlock)))
              }
            />
          ) : showReEngage ? (
            <ReEngagementBanner key="re-engage" daysSince={stats.daysSinceLastEstimate ?? 0} />
          ) : showComeback ? (
            <ComebackBanner key="comeback" />
          ) : null}
        </AnimatePresence>

        {/* Competitions — every module the manager enables is auto-active.
            One live row each; no join/leave. */}
        <ActiveCompetitionsCard competitions={stats.competitions} />

        <FooterLinks onReplayTour={() => setTourDismissed(false)} />
      </motion.div>
    </div>
  )
}

function unlockKey(u: RecentUnlock): string {
  return `${u.trigger}-${u.hoursSince}`
}

/* ═══════════════ COUNT UP / ANIMATED FLAME ═══════════════ */

function CountUp({
  value,
  duration = 0.8,
  className,
}: {
  value: number
  duration?: number
  className?: string
}) {
  const mv = useMotionValue(0)
  const spring = useSpring(mv, { duration: duration * 1000, bounce: 0.05 })
  const display = useTransform(spring, (v) => Math.round(v).toString())
  useEffect(() => {
    mv.set(value)
  }, [mv, value])
  return <motion.span className={className}>{display}</motion.span>
}

function AnimatedFlame({ size = 16, active }: { size?: number; active: boolean }) {
  return (
    <motion.svg
      width={size}
      height={size}
      viewBox="0 0 20 24"
      animate={
        active
          ? { scale: [1, 1.06, 0.96, 1.03, 1], opacity: [1, 0.92, 1, 0.95, 1] }
          : { scale: 1, opacity: 0.7 }
      }
      transition={
        active
          ? { duration: 1.6, repeat: Infinity, ease: "easeInOut" }
          : { duration: 0.4 }
      }
      aria-hidden
    >
      <defs>
        <linearGradient id="flameGrad" x1="0" y1="1" x2="0" y2="0">
          <stop offset="0" stopColor="#f59e0b" />
          <stop offset="0.5" stopColor="#f97316" />
          <stop offset="1" stopColor="#fde68a" />
        </linearGradient>
        <linearGradient id="flameCore" x1="0" y1="1" x2="0" y2="0">
          <stop offset="0" stopColor="#fbbf24" />
          <stop offset="1" stopColor="#fef3c7" />
        </linearGradient>
      </defs>
      <path
        d="M10 2 C 13 6, 17 9, 17 14 C 17 19, 14 22, 10 22 C 6 22, 3 19, 3 14 C 3 10, 6 9, 7 6 C 8 8, 9 10, 10 7 Z"
        fill="url(#flameGrad)"
      />
      <motion.path
        d="M10 8 C 12 11, 14 13, 14 16 C 14 19, 12 21, 10 21 C 8 21, 6 19, 6 16 C 6 13, 8 13, 9 10 Z"
        fill="url(#flameCore)"
        animate={active ? { opacity: [0.85, 1, 0.9, 1, 0.85] } : { opacity: 0.85 }}
        transition={
          active ? { duration: 1.2, repeat: Infinity, ease: "easeInOut" } : { duration: 0.4 }
        }
      />
    </motion.svg>
  )
}

/* ═══════════════ THE COCKPIT BAND ═══════════════ */

// "Thu · 1 workday left this week" — factual week context, no cheerleading.
function weekContext(): string {
  const n = new Date()
  const wd = n.toLocaleDateString(undefined, { weekday: "short" })
  const iso = n.getDay() === 0 ? 7 : n.getDay() // Mon=1 … Sun=7
  const left = Math.max(0, 5 - iso)
  if (iso > 5) return `${wd} · new week Monday`
  if (left === 0) return `${wd} · last workday of the week`
  return `${wd} · ${left} workday${left === 1 ? "" : "s"} left this week`
}

// The advisor's day in the product's signature voice: charcoal band,
// emerald money, tabular numerals, quiet uppercase labels. Composes what
// used to be four separate pieces (greeting pills, stat tiles, goal card,
// streak card) into one designed object.
function AdvisorHeroBand({
  firstName,
  bell,
  stats,
  showStreak,
}: {
  firstName: string
  bell: React.ReactNode
  stats: AdvisorStats
  showStreak: boolean
}) {
  const now = new Date()
  const day = now.toLocaleDateString(undefined, {
    weekday: "short",
    month: "short",
    day: "numeric",
  })
  const hour = now.getHours()
  const salute = hour < 12 ? "Mornin'" : hour < 17 ? "Afternoon" : "Evening"

  const weekly = stats.week.perAdvisorTarget
  const pace = weekly > 0 ? Math.max(1, Math.ceil(weekly / 5)) : null
  const done = stats.today.count
  const pct = pace ? Math.min(100, Math.round((done / pace) * 100)) : null
  const banked = stats.earningsByPeriod.today
  const pendingToday = stats.earningsByPeriod.pendingToday
  const streak = stats.streak.currentDays

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
      className="relative rounded-3xl bg-gradient-to-br from-zinc-900 via-zinc-900 to-zinc-950 text-white shadow-xl shadow-zinc-900/25 ring-1 ring-white/[0.07]"
    >
      {/* Depth lives in its own clipped layer (the band itself must let the
          activity sheet trigger overflow): a money glow, a cool counter-glow,
          and a top hairline shine. */}
      <div aria-hidden className="absolute inset-0 rounded-3xl overflow-hidden">
        <div className="absolute -top-28 -right-20 size-72 rounded-full bg-emerald-500/[0.16] blur-3xl" />
        <div className="absolute -bottom-32 -left-24 size-72 rounded-full bg-sky-500/[0.07] blur-3xl" />
        <div className="absolute inset-x-6 top-0 h-px bg-gradient-to-r from-transparent via-white/20 to-transparent" />
      </div>

      <div className="relative px-5 pt-5 pb-4">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <p className="text-[10.5px] font-bold uppercase tracking-[0.18em] text-zinc-400">
              {day}
            </p>
            <p className="text-[18px] font-bold text-white mt-0.5 truncate">
              {salute}, {firstName}
            </p>
          </div>
          {/* The bell keeps its own white-chip styling — a deliberate light
              object on the charcoal, and its dropdown owns its colors. */}
          <div className="shrink-0">{bell}</div>
        </div>

        {/* The day, in numbers: work left, money right. */}
        <div className="mt-5 flex items-end justify-between gap-4">
          <div className="min-w-0">
            <p className="text-[46px] font-black tabular-nums leading-none tracking-tight">
              <CountUp value={done} />
              {pace !== null && (
                <span className="text-[21px] font-bold text-zinc-400"> / {pace}</span>
              )}
            </p>
            <p className="text-[10.5px] font-bold uppercase tracking-[0.16em] text-zinc-400 mt-2">
              Vehicles today
            </p>
          </div>
          <div className="text-right shrink-0 pb-0.5">
            <p className="text-[26px] font-black tabular-nums text-emerald-400 leading-none">
              $<CountUp value={banked} />
            </p>
            <p className="text-[10.5px] font-bold uppercase tracking-[0.16em] text-zinc-400 mt-1.5">
              Banked today
            </p>
            {pendingToday > 0 && (
              <p className="text-[11px] text-zinc-300 mt-1 tabular-nums">
                +${pendingToday.toLocaleString("en-US")} on open work
              </p>
            )}
          </div>
        </div>

        {pct !== null && (
          <div className="mt-4">
            {/* Pace as tactile segments — one per vehicle — when the day's
                target is human-sized; a bar past that. Filling a segment
                FEELS like progress in a way a sliver of bar never does. */}
            {pace! <= 8 ? (
              <div className="flex items-center gap-1.5">
                {Array.from({ length: pace! }).map((_, i) => (
                  <motion.div
                    key={i}
                    initial={{ scaleX: 0.4, opacity: 0 }}
                    animate={{ scaleX: 1, opacity: 1 }}
                    transition={{ delay: 0.15 + i * 0.06, duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
                    className={cx(
                      "h-2.5 flex-1 rounded-full origin-left",
                      i < done
                        ? "bg-gradient-to-r from-emerald-500 to-emerald-400 shadow-[0_0_10px_rgba(16,185,129,0.35)]"
                        : "bg-white/10",
                    )}
                  />
                ))}
              </div>
            ) : (
              <div className="h-2.5 rounded-full bg-white/10 overflow-hidden">
                <motion.div
                  initial={{ width: 0 }}
                  animate={{ width: `${pct}%` }}
                  transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1], delay: 0.15 }}
                  className="h-full rounded-full bg-gradient-to-r from-emerald-500 to-emerald-400"
                />
              </div>
            )}
            {/* Earned, not needy: a done day is a quiet tick and a fact. */}
            <p className="mt-2 text-[11.5px] text-zinc-400 flex items-center gap-1.5">
              {pct >= 100 ? (
                <>
                  <CheckCircle className="size-3.5 text-emerald-400 shrink-0" aria-hidden />
                  <span className="font-semibold text-emerald-400">Day made</span>
                  <span className="text-zinc-500">· {weekContext()}</span>
                </>
              ) : (
                <>
                  <span>
                    {pace! - done} to go today
                  </span>
                  <span className="text-zinc-500">· {weekContext()}</span>
                </>
              )}
            </p>
          </div>
        )}
      </div>

      {/* The rail — streak · week · month, band-stat idiom. */}
      <div className="relative border-t border-white/10 px-5 py-4 flex items-center gap-8">
        {showStreak && streak > 0 && (
          <div className="flex items-center gap-2">
            <AnimatedFlame size={20} active={streak >= 5} />
            <div>
              <p className="text-[17px] font-black tabular-nums text-amber-300 leading-none">
                {streak}
              </p>
              <p className="text-[9.5px] font-bold uppercase tracking-[0.14em] text-zinc-400 mt-1">
                Day streak
              </p>
            </div>
          </div>
        )}
        <div>
          <p className="text-[17px] font-black tabular-nums text-white leading-none">
            {stats.week.count}
          </p>
          <p className="text-[9.5px] font-bold uppercase tracking-[0.14em] text-zinc-400 mt-1">
            This week
          </p>
        </div>
        <div>
          <p className="text-[17px] font-black tabular-nums text-emerald-400 leading-none">
            ${stats.earningsByPeriod.month.toLocaleString("en-US")}
          </p>
          <p className="text-[9.5px] font-bold uppercase tracking-[0.14em] text-zinc-400 mt-1">
            This month
          </p>
        </div>
        <div className="ml-auto text-right">
          <p className="text-[17px] font-black tabular-nums text-white leading-none">
            ${stats.earnings.lifetimeAmount.toLocaleString("en-US")}
          </p>
          <p className="text-[9.5px] font-bold uppercase tracking-[0.14em] text-zinc-400 mt-1">
            All time
          </p>
        </div>
      </div>
    </motion.div>
  )
}

/* ═══════════════ WALLET GLANCE PILL ═══════════════ */

// Compact, tap-to-expand link to /wallet. Shows the two numbers that
// matter at a glance — available (settled) + pending (this cycle) —
// and the next-payout date. Doesn't steal focus from the hero CTA below.
function WalletGlance({
  available,
  pending,
  nextPayoutDate,
}: {
  available: number
  pending: number
  nextPayoutDate: Date
}) {
  const now = new Date()
  const days = Math.max(
    0,
    Math.ceil((nextPayoutDate.getTime() - now.getTime()) / (24 * 60 * 60 * 1000)),
  )
  const payoutLabel =
    days === 0
      ? "Payout today"
      : days === 1
        ? "Payout tomorrow"
        : `Payout in ${days} days`
  return (
    <motion.div variants={RISE}>
      <Link
        to="/wallet"
        className="group flex items-center gap-3 rounded-2xl border border-zinc-200/70 bg-white px-4 py-3 shadow-sm hover:border-zinc-300 hover:shadow transition-all duration-150"
      >
        <div className="size-9 rounded-xl bg-gradient-to-br from-emerald-500 to-emerald-700 grid place-items-center shadow-md shadow-emerald-600/30 shrink-0">
          <Wallet01 className="size-4 text-white" aria-hidden />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-baseline gap-1.5">
            <span className="text-[18px] font-black text-zinc-900 leading-none tabular-nums">
              ${available.toLocaleString("en-US")}
            </span>
            <span className="text-[11.5px] font-semibold text-zinc-500">available</span>
            {pending > 0 && (
              <span className="text-[11.5px] text-zinc-400 tabular-nums ml-0.5">
                · ${pending} pending
              </span>
            )}
          </div>
          <p className="text-[11px] text-zinc-500 mt-0.5">{payoutLabel}</p>
        </div>
        <ChevronRight
          className="size-4 text-zinc-400 group-hover:text-zinc-700 group-hover:translate-x-0.5 transition-all duration-150 shrink-0"
          aria-hidden
        />
      </Link>
    </motion.div>
  )
}

/* ═══════════════ HERO ═══════════════ */

// The work the advisor came here to do. Big, unambiguous, can't be missed.
// Subtitle surfaces the per-save pay so the value is anchored at the moment
// of action — not just a number on a separate screen.
function StartEstimateHero({
  href,
  perEstimate,
  perPaid,
}: {
  href: string
  perEstimate: number
  perPaid: number
}) {
  const subtitle = (() => {
    if (perEstimate > 0 && perPaid > 0)
      return `+$${perEstimate} in your wallet on save · +$${perPaid} when paid`
    if (perEstimate > 0) return `+$${perEstimate} in your wallet on save`
    if (perPaid > 0) return `+$${perPaid} when the body shop completes it`
    return "40 seconds, lane-side"
  })()
  return (
    <motion.div variants={RISE} className="relative">
      <div
        aria-hidden
        className="absolute -inset-1 rounded-[28px] bg-gradient-to-br from-emerald-300/40 via-emerald-200/20 to-transparent blur-2xl"
      />
      <Link
        to={href}
        className="relative block overflow-hidden rounded-3xl bg-gradient-to-br from-emerald-500 via-emerald-600 to-emerald-800 shadow-2xl shadow-emerald-700/30 active:scale-[0.99] transition-transform duration-150"
      >
        <div
          aria-hidden
          className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(255,255,255,0.18),transparent_55%)]"
        />
        <motion.span
          aria-hidden
          initial={{ x: "-100%" }}
          animate={{ x: "200%" }}
          transition={{ duration: 2.4, ease: "easeInOut", repeat: Infinity, repeatDelay: 6 }}
          className="absolute inset-y-0 w-1/3 bg-gradient-to-r from-transparent via-white/15 to-transparent skew-x-12 pointer-events-none"
        />
        <div className="relative flex items-center gap-4 p-6 md:p-7">
          <div className="size-16 md:size-[68px] rounded-2xl bg-white/15 backdrop-blur grid place-items-center shrink-0 shadow-inner">
            <ZapFast className="size-8 text-white" aria-hidden />
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-emerald-100/80">
              Next mission
            </p>
            <p className="text-[26px] md:text-[28px] font-black text-white leading-tight tracking-tight">
              Start an estimate
            </p>
            <p className="text-[13.5px] md:text-[14px] text-emerald-50/90 mt-1 leading-snug">
              {subtitle}
            </p>
          </div>
          <ArrowRight className="size-7 text-white/80 shrink-0" aria-hidden />
        </div>
      </Link>
    </motion.div>
  )
}

/* ═══════════════ EARNINGS CARD (TODAY / WEEK / MONTH SWITCHER) ═══════════════ */

type EarningsPeriod = "today" | "week" | "month"

const PERIOD_LABEL: Record<EarningsPeriod, string> = {
  today: "Today",
  week: "Week",
  month: "Month",
}

// One card, three views — the Revolut "switch account" pattern applied
// to time windows. Lead with two big tabular numbers (estimates + earned)
// side-by-side so the work and the pay land in the same glance.
function EarningsCard({ stats }: { stats: AdvisorStats }) {
  const [period, setPeriod] = useState<EarningsPeriod>("today")

  // For the active period: settled earnings + estimate count + paid count
  // + the open-estimate pending tally (per-paid sitting on open work). All
  // read from earningsByPeriod, which sums the UNCAPPED event stream — the
  // pending figures never undercount the way the 20-event display feed would
  // for an active advisor.
  const view = useMemo(() => {
    const eb = stats.earningsByPeriod
    if (period === "today") {
      return {
        earned: eb.today,
        estimateCount: stats.today.count,
        paidCount: eb.todayPaidCount,
        pendingCount: eb.pendingTodayCount,
        pendingAmount: eb.pendingToday,
      }
    }
    if (period === "week") {
      return {
        earned: eb.week,
        estimateCount: stats.week.count,
        paidCount: eb.weekPaidCount,
        pendingCount: eb.pendingWeekCount,
        pendingAmount: eb.pendingWeek,
      }
    }
    return {
      earned: eb.month,
      estimateCount: stats.month.count,
      paidCount: eb.monthPaidCount,
      pendingCount: eb.pendingMonthCount,
      pendingAmount: eb.pendingMonth,
    }
  }, [period, stats])

  const hasActivity = view.estimateCount > 0 || view.earned > 0

  return (
    <motion.div
      variants={RISE}
      className="relative overflow-hidden rounded-3xl border border-zinc-200/70 bg-white shadow-sm"
    >
      {/* Faint emerald wash for warmth without going dark mode. */}
      <div
        aria-hidden
        className="absolute inset-x-0 top-0 h-32 bg-gradient-to-b from-emerald-50/60 to-transparent pointer-events-none"
      />
      <div className="relative p-5 md:p-6">
        {/* Switcher — chip-style segmented control */}
        <div className="flex items-center gap-1 mb-5 p-1 rounded-xl bg-zinc-100 w-fit">
          {(["today", "week", "month"] as const).map((p) => {
            const active = p === period
            return (
              <button
                key={p}
                type="button"
                onClick={() => setPeriod(p)}
                className={cx(
                  "px-3 h-7 rounded-lg text-[11.5px] font-bold uppercase tracking-wide transition-all duration-150",
                  active
                    ? "bg-white text-zinc-900 shadow-sm"
                    : "text-zinc-500 hover:text-zinc-700",
                )}
                aria-pressed={active}
              >
                {PERIOD_LABEL[p]}
              </button>
            )
          })}
        </div>

        {/* Two big stacked numbers — Estimates / Earned. Both tabular-nums
            and both prominent so the eye registers "I wrote N and got $X"
            in one beat. */}
        <div className="grid grid-cols-2 gap-4 mb-3">
          <div>
            <p className="text-[10.5px] font-bold uppercase tracking-[0.14em] text-zinc-400 mb-1">
              Estimates
            </p>
            <CountUp
              value={view.estimateCount}
              className={cx(
                "block text-[44px] md:text-[52px] font-black leading-none tracking-tight tabular-nums",
                view.estimateCount === 0 ? "text-zinc-300" : "text-zinc-900",
              )}
            />
            <p className="text-[11.5px] text-zinc-500 mt-1.5 leading-tight">
              {view.paidCount > 0 ? (
                <>
                  <span className="font-semibold text-emerald-700">
                    {view.paidCount} paid
                  </span>{" "}
                  by body shop
                </>
              ) : (
                "Not paid yet"
              )}
            </p>
          </div>
          <div>
            <p className="text-[10.5px] font-bold uppercase tracking-[0.14em] text-emerald-700 mb-1">
              Earned
            </p>
            <span
              className={cx(
                "block text-[44px] md:text-[52px] font-black leading-none tracking-tight tabular-nums",
                view.earned > 0
                  ? "bg-gradient-to-br from-emerald-600 to-emerald-800 bg-clip-text text-transparent"
                  : "text-zinc-300",
              )}
            >
              <span aria-hidden>$</span>
              <CountUp value={view.earned} className="inline" />
            </span>
            <p className="text-[11.5px] text-zinc-500 mt-1.5 leading-tight">
              {view.earned === 0 ? "No earnings yet" : "Settled in your wallet"}
            </p>
          </div>
        </div>

        {/* Pending pill — money sitting on open estimates in this window. */}
        {view.pendingCount > 0 && (
          <div className="mt-2 inline-flex items-center gap-2 h-8 pl-2 pr-3 rounded-full border border-amber-200 bg-amber-50 text-amber-900 text-[12px] font-semibold">
            <span className="size-1.5 rounded-full bg-amber-500" aria-hidden />
            <span className="tabular-nums font-bold">${view.pendingAmount}</span>
            <span className="opacity-80">
              pending on {view.pendingCount} open
            </span>
          </div>
        )}

        {/* Empty-state micro-prompt — keeps the card from looking dead when
            the advisor opens it before they've worked today. */}
        {!hasActivity && (
          <p className="mt-1 text-[12px] text-zinc-500 leading-snug">
            Write your first estimate of the {period} to seed the count.
          </p>
        )}
      </div>
    </motion.div>
  )
}

/* ═══════════════ RECENT ESTIMATES ═══════════════ */

// Five most recent estimates with status + per-estimate earned + pending
// indicator. Bridges work-to-money — the advisor can see exactly which
// open estimate is sitting unconverted.
function RecentEstimatesList({
  estimates,
  currentUserId,
  perEstimate,
  perPaid,
  duplicateVinIds,
}: {
  estimates: ApiEstimate[]
  currentUserId: number | null
  perEstimate: number
  perPaid: number
  // Estimates whose per-scan $ was withheld by the 30-day same-VIN dedupe.
  duplicateVinIds: Set<number>
}) {
  const mine = useMemo(() => {
    return estimates
      .filter((e) => currentUserId != null && e.createdBy?.id === currentUserId)
      .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
      .slice(0, 5)
  }, [estimates, currentUserId])

  if (mine.length === 0) return null

  return (
    <motion.div
      variants={RISE}
      className="rounded-2xl border border-zinc-200/70 bg-white shadow-sm overflow-hidden"
    >
      <div className="px-5 pt-4 pb-2 flex items-center justify-between">
        <p className="text-[10.5px] font-bold uppercase tracking-[0.16em] text-zinc-400">
          Your recent estimates
        </p>
        <Link
          to="/my-estimates"
          className="text-[11.5px] font-semibold text-zinc-500 hover:text-zinc-900 inline-flex items-center gap-1"
        >
          See all
          <ArrowUpRight className="size-3" aria-hidden />
        </Link>
      </div>
      <div className="px-2 pb-2">
        {mine.map((e) => (
          <EstimateRow
            key={e.id}
            estimate={e}
            perEstimate={perEstimate}
            perPaid={perPaid}
            isDuplicate={duplicateVinIds.has(e.id)}
          />
        ))}
      </div>
    </motion.div>
  )
}

function EstimateRow({
  estimate,
  perEstimate,
  perPaid,
  isDuplicate,
}: {
  estimate: ApiEstimate
  perEstimate: number
  perPaid: number
  isDuplicate: boolean
}) {
  const isPaid = estimate.status === "Completed"
  const isLost = estimate.status === "Declined"
  const isOpen = !isPaid && !isLost

  const veh = estimate.vehicle
  const label =
    veh && veh.make && veh.model
      ? `${veh.year ? `${veh.year} ` : ""}${veh.make} ${veh.model}`
      : `Estimate #${estimate.id}`

  // Dot-state idiom, not a pill — one colored dot + a human label. Same
  // grammar the Customers book uses; no shouting caps in a money list.
  const statusDot = isPaid ? "bg-emerald-500" : isLost ? "bg-zinc-300" : "bg-amber-500"
  const statusLabel = isPaid
    ? "Paid"
    : isLost
      ? "Declined"
      : estimate.status === "InProgress"
        ? "In progress"
        : estimate.status

  return (
    <Link
      to={`/estimates/${estimate.id}`}
      className="flex items-center gap-3 px-3 py-2.5 rounded-xl hover:bg-zinc-50 transition-colors duration-100"
    >
      <div className="min-w-0 flex-1">
        <p className="text-[13.5px] font-semibold text-zinc-900 truncate">{label}</p>
        <div className="flex items-center gap-1.5 mt-1 text-[11.5px]">
          <span className={cx("size-1.5 rounded-full shrink-0", statusDot)} aria-hidden />
          <span className="font-medium text-zinc-500">{statusLabel}</span>
          <span className="text-zinc-300">·</span>
          <span className="text-zinc-400 tabular-nums">#{estimate.id}</span>
        </div>
      </div>
      <div className="text-right shrink-0">
        {/* Per-scan part — a VIN-duplicate scan still counts (streaks,
            leaderboards) but pays $0, tagged honestly. */}
        {isDuplicate ? (
          <p className="text-[10.5px] font-semibold text-zinc-400">
            duplicate — no payout
          </p>
        ) : (
          <p className="text-[13.5px] font-bold tabular-nums text-emerald-700">
            +${perEstimate}
          </p>
        )}
        {isOpen && perPaid > 0 && (
          <p className="text-[10.5px] text-amber-700 tabular-nums">+${perPaid} pending</p>
        )}
        {isPaid && perPaid > 0 && (
          <p className="text-[10.5px] text-emerald-700 tabular-nums">+${perPaid} paid</p>
        )}
        {isLost && perPaid > 0 && (
          <p className="text-[10.5px] text-zinc-400 tabular-nums">${perPaid} lost</p>
        )}
      </div>
      <ChevronRight className="size-4 text-zinc-300 shrink-0" aria-hidden />
    </Link>
  )
}

/* ═══════════════ WEEKLY LEADERBOARD ═══════════════ */

// Mon-start standings in scans + $, so the race reads in the same unit as
// the rest of the screen. Renders whenever any leaderboard layer is on —
// prize money is not required for the board to exist.
function WeeklyLeaderboardCard({
  board,
  competitions,
  memberMeta,
  storeWeeklyTarget,
}: {
  board: LeaderboardSnapshot
  competitions: CompetitionLiveState[]
  memberMeta?: Map<string, { role: string; department: string | null }>
  storeWeeklyTarget?: number
}) {
  // Podium — the top three by count (ties keep board order). Only renders
  // with 3+ entries; two people racing don't need a stage.
  const podium = board.entries.slice(0, 3)
  const teamTotal = board.entries.reduce((s, e) => s + e.count, 0)
  const teamPct =
    storeWeeklyTarget && storeWeeklyTarget > 0
      ? Math.round((teamTotal / storeWeeklyTarget) * 100)
      : null
  // Pass nudge — only when the advisor directly above is ≤5 scans ahead.
  // Prefer the competition engine's line (it owns pass semantics); fall
  // back to a computed one when the module is off but the legacy
  // leaderboard bonus keeps this card alive.
  const gap = board.myRank > 1 ? (board.closestAbove?.gap ?? null) : null
  // Closest advisor strictly above me — for the nudge's "pass {name}".
  const above =
    board.entries
      .filter((e) => e.count > board.myCount)
      .sort((a, b) => a.count - b.count)[0] ?? null
  const aboveName = above
    ? `${above.firstName} ${above.lastName.charAt(0)}.`
    : (board.closestAbove?.firstName ?? null)
  const engineNudge =
    competitions.find((c) => c.key === "weeklyVolumeLeaderboard")?.nudge ?? null
  const nudge =
    gap != null && gap <= 5 && aboveName
      ? (engineNudge ??
        `${gap + 1} more ${gap + 1 === 1 ? "scan" : "scans"} to pass ${aboveName} and take #${board.myRank - 1}`)
      : null

  return (
    <motion.div
      variants={RISE}
      className="rounded-2xl border border-zinc-200/70 bg-white shadow-sm overflow-hidden"
    >
      <div className="px-5 pt-4 pb-2 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="size-7 rounded-lg bg-gradient-to-br from-amber-400 to-amber-600 grid place-items-center shadow-md shadow-amber-500/40">
            <Trophy01 className="size-3.5 text-white" aria-hidden />
          </div>
          <p className="text-[10.5px] font-bold uppercase tracking-[0.16em] text-amber-700">
            Leaderboard
          </p>
        </div>
        <Link
          to="/leaders"
          className="inline-flex items-center gap-1 text-[11px] font-bold text-zinc-500 hover:text-zinc-900 transition-colors"
        >
          Full board <ChevronRight className="size-3" aria-hidden />
        </Link>
      </div>
      {/* Podium — top three on a stage, center seat tallest. Same data as
          the list below it; the stage is the glance, the list is the truth. */}
      {podium.length >= 3 && (
        <div className="px-5 pt-2 pb-1 flex items-end justify-center gap-3">
          {[podium[1], podium[0], podium[2]].map((e, i) => {
            const place = i === 1 ? 1 : i === 0 ? 2 : 3
            const meta = memberMeta?.get(String(e.userId))
            return (
              <div key={e.userId} className="flex-1 max-w-[120px] flex flex-col items-center min-w-0">
                <div
                  className={cx(
                    "size-9 rounded-xl grid place-items-center text-[11px] font-black shadow-md mb-1",
                    place === 1
                      ? "bg-gradient-to-br from-amber-400 to-amber-600 text-white shadow-amber-500/40"
                      : place === 2
                        ? "bg-zinc-200 text-zinc-600"
                        : "bg-orange-100 text-orange-800",
                  )}
                >
                  {e.firstName.charAt(0)}
                  {e.lastName.charAt(0)}
                </div>
                <p className={cx("text-[11.5px] font-bold truncate max-w-full", e.isYou ? "text-emerald-700" : "text-zinc-800")}>
                  {e.isYou ? "You" : `${e.firstName} ${e.lastName.charAt(0)}.`}
                </p>
                <p className="text-[10px] text-zinc-400 tabular-nums mb-1.5">
                  {e.count} {e.count === 1 ? "car" : "cars"}
                </p>
                <div
                  className={cx(
                    "w-full rounded-t-xl grid place-items-start justify-center pt-1.5",
                    place === 1
                      ? "h-16 bg-gradient-to-b from-amber-300/80 to-amber-200/50"
                      : place === 2
                        ? "h-11 bg-zinc-100"
                        : "h-8 bg-orange-100/70",
                  )}
                >
                  <span
                    className={cx(
                      "text-[15px] font-black",
                      place === 1 ? "text-amber-800" : place === 2 ? "text-zinc-500" : "text-orange-800/80",
                    )}
                  >
                    {place}
                  </span>
                </div>
              </div>
            )
          })}
        </div>
      )}

      {/* Every advisor, ranked — the CEO's "everyone sees everyone" (1.2.1).
          Ranks share on ties ("1224"-style), so two leaders both read #1. */}
      <div className={cx("px-3 pb-3 space-y-1", podium.length >= 3 && "border-t border-zinc-100 pt-2")}>
        {board.entries.map((e) => {
          const meta = memberMeta?.get(String(e.userId))
          return (
            <WeeklyBoardRow
              key={e.userId}
              rank={e.rank}
              name={e.isYou ? "You" : `${e.firstName} ${e.lastName.charAt(0)}.`}
              sub={meta ? `${meta.role}${meta.department ? ` · ${meta.department}` : ""}` : undefined}
              isYou={e.isYou}
              count={e.count}
              dollars={e.weekDollars}
            />
          )
        })}
      </div>

      {/* Team on fire — the whole store's week vs the manager's store goal. */}
      {teamPct !== null && (
        <div className="px-4 py-2.5 border-t border-amber-100 bg-amber-50/50 flex items-center gap-2.5">
          <ZapFast className="size-4 text-amber-600 shrink-0" aria-hidden />
          <div className="min-w-0 flex-1">
            <p className="text-[11.5px] font-bold text-amber-900 leading-tight">
              {teamPct >= 100 ? "Store goal smashed" : teamPct >= 85 ? "Team on fire" : "Team total"}
              <span className="font-semibold text-amber-800/80">
                {" "}
                — {teamPct}% of the weekly store goal
              </span>
            </p>
            <div className="mt-1 h-1.5 rounded-full bg-amber-100 overflow-hidden">
              <div
                className="h-full rounded-full bg-amber-500"
                style={{ width: `${Math.min(100, teamPct)}%` }}
              />
            </div>
          </div>
          <span className="text-[15px] font-black text-amber-900 tabular-nums shrink-0">
            {teamTotal}
          </span>
        </div>
      )}
      {nudge && (
        <div className="px-4 py-2.5 border-t border-emerald-100 bg-emerald-50/60">
          <p className="text-[11.5px] font-semibold text-emerald-800 leading-snug">
            {nudge}
          </p>
        </div>
      )}
    </motion.div>
  )
}

// Compact standings row — "N scans · $X". The you-row outside the top 3
// has no per-entry $ in the snapshot, so `dollars` is nullable.
function WeeklyBoardRow({
  rank,
  name,
  sub,
  isYou,
  count,
  dollars,
}: {
  rank: number
  name: string
  /** Identity line — "Role · Department". */
  sub?: string
  isYou: boolean
  count: number
  dollars: number | null
}) {
  return (
    <div
      className={cx(
        "flex items-center gap-3 px-2.5 rounded-xl",
        sub ? "py-1.5" : "h-11",
        isYou && "bg-emerald-50/70 ring-1 ring-inset ring-emerald-300/40",
      )}
    >
      <span
        className={cx(
          "size-6 rounded-full grid place-items-center text-[11px] font-black shrink-0",
          rank === 1
            ? "bg-gradient-to-br from-amber-300 to-amber-500 text-white shadow-md shadow-amber-500/40"
            : isYou
              ? "bg-gradient-to-br from-emerald-400 to-emerald-600 text-white"
              : "bg-zinc-100 text-zinc-500",
        )}
      >
        {rank}
      </span>
      <div className="flex-1 min-w-0">
        <p
          className={cx(
            "text-[13px] font-semibold truncate leading-tight",
            isYou ? "text-emerald-900" : "text-zinc-800",
          )}
        >
          {name}
        </p>
        {sub && (
          <p className="text-[10px] text-zinc-400 truncate leading-tight">{sub}</p>
        )}
      </div>
      <span
        className={cx(
          "text-[12.5px] font-bold tabular-nums",
          rank === 1 ? "text-amber-700" : isYou ? "text-emerald-700" : "text-zinc-600",
        )}
      >
        {count} {count === 1 ? "scan" : "scans"}
        {dollars != null && (
          <span
            className={cx(
              "font-semibold",
              isYou ? "text-emerald-700/80" : "text-zinc-400",
            )}
          >
            {" "}· ${dollars.toLocaleString("en-US")}
          </span>
        )}
      </span>
    </div>
  )
}

/* ═══════════════ COMPETITIONS ═══════════════ */

const COMPETITION_ICON: Record<
  CompetitionModuleKey,
  React.ComponentType<{ className?: string }>
> = {
  dailyScanChampion: Trophy01,
  firstScanOfDay: ZapFast,
  hotStreak: ZapFast,
  fastStart: Rocket02,
  weeklyVolumeLeaderboard: Trophy01,
  comebackChallenge: LineChartUp01,
  beatPersonalBest: Stars02,
  streakBonus: CalendarCheck01,
  teamGoal: Target04,
}

// Tile gradient by cadence so daily/weekly/streak/team races share a
// color family.
const CADENCE_TILE: Record<CompetitionCadence, string> = {
  daily: "from-sky-400 to-sky-600 shadow-sky-500/40",
  weekly: "from-amber-400 to-amber-600 shadow-amber-500/40",
  streak: "from-indigo-400 to-indigo-600 shadow-indigo-500/40",
  team: "from-emerald-400 to-emerald-600 shadow-emerald-500/40",
}

// Every module the manager enabled, live — auto-active, no join/leave.
function ActiveCompetitionsCard({
  competitions,
}: {
  competitions: CompetitionLiveState[]
}) {
  if (competitions.length === 0) return null
  return (
    <motion.div
      variants={RISE}
      className="rounded-2xl border border-zinc-200/70 bg-white shadow-sm overflow-hidden"
    >
      <div className="px-5 pt-4 pb-2 flex items-center justify-between">
        <p className="text-[10.5px] font-bold uppercase tracking-[0.16em] text-zinc-400">
          Competitions
        </p>
        <span className="text-[11px] font-semibold text-zinc-400 tabular-nums">
          {competitions.length} active
        </span>
      </div>
      <div className="px-3 pb-3 space-y-2">
        {competitions.map((c) => (
          <CompetitionRow key={c.key} state={c} />
        ))}
      </div>
    </motion.div>
  )
}

function CompetitionRow({ state }: { state: CompetitionLiveState }) {
  const Icon = COMPETITION_ICON[state.key]
  const footerLeft = [state.progressLabel, state.standingLabel]
    .filter(Boolean)
    .join(" · ")
  return (
    <div className="rounded-xl border border-zinc-200/60 bg-white px-3 py-3">
      <div className="flex items-start gap-3">
        <div
          className={cx(
            "size-9 rounded-xl grid place-items-center shrink-0 shadow-md bg-gradient-to-br",
            CADENCE_TILE[state.cadence],
          )}
        >
          <Icon className="size-4 text-white" aria-hidden />
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-[13px] font-semibold text-zinc-900 leading-tight truncate">
            {state.title}
          </p>
          <p className="text-[11.5px] text-zinc-500 leading-snug mt-0.5">
            {state.description}
          </p>
        </div>
        {state.prize > 0 && (
          <span className="inline-flex items-center gap-1 h-6 px-2 rounded-full border border-amber-300/70 bg-amber-50 text-amber-900 text-[11px] font-bold shrink-0">
            <Coins03 className="size-3 opacity-80" aria-hidden />
            ${state.prize}
          </span>
        )}
      </div>
      {state.progressPct != null && (
        <div className="mt-2.5 h-1.5 rounded-full bg-zinc-100 overflow-hidden">
          <motion.div
            initial={{ width: 0 }}
            animate={{ width: `${Math.min(100, state.progressPct)}%` }}
            transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
            className="h-full rounded-full bg-gradient-to-r from-emerald-400 to-emerald-600"
          />
        </div>
      )}
      <div className="mt-2 flex items-center justify-between gap-3">
        <p className="text-[11px] text-zinc-500 leading-snug truncate">{footerLeft}</p>
        <span className="text-[11px] font-semibold text-amber-700 shrink-0">
          {state.endsLabel}
        </span>
      </div>
    </div>
  )
}

/* ═══════════════ FRESH PAY BANNER ═══════════════ */

// Fires when a body-shop completion landed in the last 24h. The single
// highest-payoff celebration moment on the dashboard — bigger reward,
// rarer signal. Activation milestones get their own flip animation in
// the badges tile when freshly earned.
function FreshPayBanner({
  event,
  onDismiss,
}: {
  event: import("@/lib/dashboard/advisor-stats").WalletEvent
  onDismiss: () => void
}) {
  return (
    <motion.div
      initial={{ opacity: 0, y: -8, scale: 0.98 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, y: -8, scale: 0.98 }}
      transition={{ type: "spring", stiffness: 280, damping: 24 }}
      className="relative overflow-hidden rounded-2xl border-2 border-amber-300/50 bg-gradient-to-br from-amber-50 via-emerald-50/40 to-amber-50 shadow-xl shadow-amber-600/15"
    >
      <motion.span
        aria-hidden
        initial={{ x: "-100%" }}
        animate={{ x: "200%" }}
        transition={{ duration: 2.4, ease: "easeInOut", repeat: Infinity, repeatDelay: 5 }}
        className="absolute inset-y-0 w-1/3 bg-gradient-to-r from-transparent via-white/70 to-transparent skew-x-12 pointer-events-none"
      />
      <div className="relative px-4 py-3.5 flex items-center gap-3">
        <div className="relative">
          <div aria-hidden className="absolute inset-0 rounded-2xl blur-md bg-amber-400/50" />
          <div className="relative size-11 rounded-2xl bg-gradient-to-br from-amber-400 to-amber-600 grid place-items-center shrink-0 shadow-lg shadow-amber-500/50">
            <Coins03 className="size-5 text-white" aria-hidden />
          </div>
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-[10.5px] font-bold uppercase tracking-[0.14em] text-amber-800">
            Paid · just now
          </p>
          <p className="text-[14.5px] font-bold text-zinc-900 leading-tight mt-0.5 truncate">
            {event.label}{" "}
            <span className="text-emerald-700">+${event.amount.toLocaleString("en-US")}</span>
          </p>
        </div>
        <button
          type="button"
          onClick={onDismiss}
          aria-label="Dismiss"
          className="size-8 grid place-items-center rounded-lg text-zinc-400 hover:text-zinc-900 hover:bg-white/60 transition-colors duration-150 shrink-0"
        >
          <X className="size-3.5" aria-hidden />
        </button>
      </div>
    </motion.div>
  )
}

/* ═══════════════ BANNERS ═══════════════ */

function GoalHitBanner({
  kind,
  count,
  target,
  moneyEarned,
  streakBonusMoney,
  onDismiss,
}: {
  kind: "week" | "month"
  count: number
  target: number
  moneyEarned: number
  streakBonusMoney: number
  onDismiss: () => void
}) {
  const bonus = count - target
  const isMonth = kind === "month"
  // Monthly hit is rarer and bigger — use a richer multi-stop gradient,
  // a thicker border, and a different eyebrow so the celebration scales
  // with the achievement.
  return (
    <motion.div
      initial={{ opacity: 0, y: -8, scale: 0.98 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, y: -8, scale: 0.98 }}
      transition={{ type: "spring", stiffness: 280, damping: 24 }}
      className={cx(
        "relative overflow-hidden rounded-2xl border-2 shadow-xl",
        isMonth
          ? "border-amber-400/60 bg-gradient-to-br from-amber-100 via-amber-50 to-emerald-50 shadow-amber-500/20"
          : "border-amber-300/50 bg-gradient-to-br from-amber-50 via-emerald-50/60 to-amber-50 shadow-amber-500/15",
      )}
    >
      <motion.span
        aria-hidden
        initial={{ x: "-100%" }}
        animate={{ x: "200%" }}
        transition={{
          duration: isMonth ? 2.0 : 2.4,
          delay: 0.4,
          ease: "easeInOut",
          repeat: Infinity,
          repeatDelay: isMonth ? 4 : 6,
        }}
        className="absolute inset-y-0 w-1/3 bg-gradient-to-r from-transparent via-white/70 to-transparent skew-x-12 pointer-events-none"
      />
      <div className="relative px-5 py-4 flex items-center gap-3">
        <div className="relative">
          <div aria-hidden className={cx("absolute inset-0 rounded-2xl blur-md", isMonth ? "bg-amber-400/60" : "bg-amber-400/40")} />
          <div
            className={cx(
              "relative grid place-items-center shrink-0 shadow-lg bg-gradient-to-br shadow-amber-500/50",
              isMonth
                ? "size-14 rounded-2xl from-amber-400 via-amber-500 to-amber-700"
                : "size-12 rounded-2xl from-amber-400 to-amber-600",
            )}
          >
            <Trophy01 className={cx(isMonth ? "size-7" : "size-6", "text-white")} aria-hidden />
          </div>
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-[10.5px] font-bold uppercase tracking-[0.14em] text-amber-800">
            {isMonth ? "Monthly goal · cleared" : "Weekly goal · cleared"}
          </p>
          <p className="text-[15px] font-black text-zinc-900 leading-tight mt-0.5">
            {isMonth
              ? `${count} of ${target} for the month — huge.`
              : `${count} of ${target} — nice work.`}
          </p>
          {moneyEarned > 0 && (
            <p className="text-[11.5px] text-emerald-800 font-semibold mt-0.5">
              You earned ${moneyEarned.toLocaleString("en-US")}.
              {!isMonth && streakBonusMoney > 0 && (
                <>
                  {" "}Stack 4 weeks in a row → ${streakBonusMoney.toLocaleString("en-US")} bonus.
                </>
              )}
            </p>
          )}
          {bonus > 0 && (
            <p className="text-[11px] text-amber-800 font-semibold mt-1">
              +{bonus} extra this {isMonth ? "month" : "week"}
            </p>
          )}
        </div>
        <button
          type="button"
          onClick={onDismiss}
          aria-label="Dismiss"
          className="size-8 grid place-items-center rounded-lg text-zinc-400 hover:text-zinc-900 hover:bg-white/60 transition-colors duration-150 shrink-0"
        >
          <X className="size-3.5" aria-hidden />
        </button>
      </div>
    </motion.div>
  )
}

const TRIGGER_LABEL: Record<DealershipRewardTrigger, { name: string; icon: React.ComponentType<{ className?: string }> }> = {
  activationFirstEstimate: { name: "On the board", icon: Rocket02 },
  activation5DayStreak: { name: "5-day milestone", icon: Stars02 },
  activation30DayStreak: { name: "30-day milestone", icon: Award04 },
  weeklyGoalHit: { name: "Weekly goal", icon: Trophy01 },
  monthlyGoalHit: { name: "Monthly goal", icon: Trophy01 },
  weeklyStreak4Bonus: { name: "4-week run", icon: CalendarCheck01 },
}

function UnlockBanner({
  unlock,
  onDismiss,
}: {
  unlock: RecentUnlock
  onDismiss: () => void
}) {
  const meta = TRIGGER_LABEL[unlock.trigger]
  const Icon = meta.icon
  const label = unlock.bundle?.badge?.label || meta.name
  const money =
    unlock.bundle?.monetary?.enabled && unlock.bundle.monetary.amount
      ? unlock.bundle.monetary.amount
      : 0
  return (
    <motion.div
      initial={{ opacity: 0, y: -8, scale: 0.98 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, y: -8, scale: 0.98 }}
      transition={{ type: "spring", stiffness: 280, damping: 24 }}
      className="relative overflow-hidden rounded-2xl border border-amber-300/40 bg-gradient-to-br from-amber-50 via-emerald-50 to-emerald-50 shadow-xl shadow-amber-600/15"
    >
      <motion.span
        aria-hidden
        initial={{ x: "-100%" }}
        animate={{ x: "200%" }}
        transition={{ duration: 1.8, delay: 0.2, ease: "easeInOut" }}
        className="absolute inset-y-0 w-1/3 bg-gradient-to-r from-transparent via-white/60 to-transparent skew-x-12 pointer-events-none"
      />
      <div className="relative px-4 py-3.5 flex items-center gap-3">
        <div className="size-11 rounded-2xl grid place-items-center shrink-0 shadow-md bg-gradient-to-br from-amber-400 to-amber-600 shadow-amber-500/40">
          <Icon className="size-5 text-white" aria-hidden />
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-[10.5px] font-bold uppercase tracking-[0.14em] text-amber-800">
            Just unlocked
          </p>
          <p className="text-[14.5px] font-bold text-zinc-900 leading-tight mt-0.5 truncate">
            {label}
            {money > 0 && (
              <span className="text-emerald-700 ml-1.5">
                +${money.toLocaleString("en-US")}
              </span>
            )}
          </p>
        </div>
        <button
          type="button"
          onClick={onDismiss}
          aria-label="Dismiss"
          className="size-8 grid place-items-center rounded-lg text-zinc-400 hover:text-zinc-900 hover:bg-white/60 transition-colors duration-150 shrink-0"
        >
          <X className="size-3.5" aria-hidden />
        </button>
      </div>
    </motion.div>
  )
}

function ReEngagementBanner({ daysSince }: { daysSince: number }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: -8 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -8 }}
      transition={{ type: "spring", stiffness: 280, damping: 24 }}
      className="relative rounded-2xl border border-zinc-200 bg-white shadow-sm"
    >
      <div className="px-4 py-3.5 flex items-center gap-3">
        <div className="size-11 rounded-2xl grid place-items-center shrink-0 shadow-md bg-gradient-to-br from-sky-400 to-sky-600 shadow-sky-500/30">
          <CalendarCheck01 className="size-5 text-white" aria-hidden />
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-[14px] font-bold text-zinc-900 leading-tight">Welcome back</p>
          <p className="text-[12px] text-zinc-500 leading-snug mt-0.5">
            {daysSince === 1
              ? "Last estimate was yesterday. Pick up where you left off."
              : `Been ${daysSince} days since your last estimate.`}
          </p>
        </div>
      </div>
    </motion.div>
  )
}

function ComebackBanner() {
  return (
    <motion.div
      initial={{ opacity: 0, y: -8 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -8 }}
      transition={{ type: "spring", stiffness: 280, damping: 24 }}
      className="relative rounded-2xl border border-emerald-200/70 bg-gradient-to-br from-emerald-50 to-white shadow-sm"
    >
      <div className="px-4 py-3.5 flex items-center gap-3">
        <div className="size-11 rounded-2xl grid place-items-center shrink-0 shadow-md bg-gradient-to-br from-emerald-400 to-emerald-600 shadow-emerald-500/30">
          <CheckCircle className="size-5 text-white" aria-hidden />
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-[14px] font-bold text-zinc-900 leading-tight">Back at it</p>
          <p className="text-[12px] text-zinc-500 leading-snug mt-0.5">
            Recent activity is rolling — your wallet is filling.
          </p>
        </div>
      </div>
    </motion.div>
  )
}

/* ═══════════════ FOOTER ═══════════════ */

function FooterLinks({ onReplayTour }: { onReplayTour: () => void }) {
  return (
    <motion.div
      variants={RISE}
      className="pt-2 pb-4 flex items-center justify-between gap-3 text-[12.5px] font-semibold text-zinc-500"
    >
      <Link
        to="/my-estimates"
        className="inline-flex items-center gap-1 hover:text-zinc-900 transition-colors duration-150"
      >
        View all my estimates
        <ArrowUpRight className="size-3.5" aria-hidden />
      </Link>
      <button
        type="button"
        onClick={onReplayTour}
        className="inline-flex items-center gap-1 hover:text-zinc-900 transition-colors duration-150"
      >
        How rewards work
        <ArrowUpRight className="size-3.5" aria-hidden />
      </button>
    </motion.div>
  )
}
